import {
  OrderType,
  QuestionGroupType,
  useSearchQuestionsByLosQuery,
} from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import { ColorPalette, ErrorState, NoRowsOverlay, Text } from '@shared/components';
import { useHasPermission } from '@shared/permissions';
import { useMemo } from 'react';

import { LOSQuestionsDataGrid } from '../../components';
import { useLOSQuestionListParams } from '../../hooks';

interface LOSQuestionListProps {
  losId: string;
  structureId: string;
}

export default function LOSQuestionList(props: Readonly<LOSQuestionListProps>) {
  const { losId, structureId } = props;

  const hasQuestionView = useHasPermission(APP_PERMISSIONS.QBANK.QUESTION.VIEW);

  const [{ page, pageSize }, { changePageNumber, changePageSize }] = useLOSQuestionListParams();

  const { data, loading } = useSearchQuestionsByLosQuery({
    skip: !hasQuestionView,
    variables: {
      search: {
        learningObjectiveStatementId: losId,
      },
      order: {
        field: 'updatedAt',
        type: OrderType.Desc,
      },
      paging: {
        currentPage: page,
        pageSize,
      },
    },
  });

  const rows = useMemo(() => {
    const questions = data?.qctSearchQuestionsInLearningObjectiveStatement.questions ?? [];

    return questions.map((q) => {
      const isItemSet = q.questionGroup?.type === QuestionGroupType.ItemSet;

      return {
        id: q.id,
        refCode: q.refCode,
        type: (isItemSet ? 'item-set' : 'single') as 'item-set' | 'single',
        itemSetName: isItemSet ? q.questionGroup?.name : '-',
        status: q.status,
        questionGroupId: q.questionGroup?.id,
      };
    });
  }, [data?.qctSearchQuestionsInLearningObjectiveStatement.questions]);

  const total = data?.qctSearchQuestionsInLearningObjectiveStatement.total ?? 0;

  const handleRowClick = (row: (typeof rows)[number]) => {
    const url =
      row.type === 'item-set' && row.questionGroupId
        ? `/qbank-structures/${structureId}/qcontents/item-set/${row.questionGroupId}/${row.id}`
        : `/qbank-structures/${structureId}/qcontents/single/${row.id}`;
    window.open(url, '_blank');
  };

  return (
    <>
      <Text variant='body1Semibold'>Total Questions in LOS: {total} Questions</Text>
      <LOSQuestionsDataGrid
        rows={rows}
        loading={loading}
        rowCount={total}
        paginationMode='server'
        onRowClick={({ row }) => handleRowClick(row)}
        paginationModel={{
          page,
          pageSize,
        }}
        slots={{
          noRowsOverlay: () =>
            hasQuestionView ? (
              <NoRowsOverlay title='ยังไม่มีคำถามที่ใช้งานวัตถุประสงค์การเรียนรู้ (LOS) นี้' />
            ) : (
              <ErrorState
                title='ไม่มีสิทธิ์การเข้าถึง'
                description='กรุณาติดต่อผู้ดูแลระบบ เพื่อขอสิทธิ์การเข้าถึง'
                slotProps={{
                  icon: {
                    color: ColorPalette.PRIMARY,
                  },
                }}
              />
            ),
        }}
        slotProps={{
          footer: {
            total,
            pagination: {
              page,
              count: total,
              rowsPerPage: pageSize,
              onPageChange: (_, newPage) => {
                changePageNumber(newPage);
              },
              onRowsPerPageChange: (event) => {
                changePageSize(parseInt(event.target.value));
              },
            },
          },
        }}
      />
    </>
  );
}
